import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { firstValueFrom, Subject, takeUntil, finalize, Observable, map } from 'rxjs';
import { GetUserRequestParams, UserData } from '../../interfaces/users.interface';
import { Router } from '@angular/router';
import { UsersService } from '../../services/users/users.service';
import { getOffset, showNotification, toTitleCase } from 'src/app/shared/helpers/helpers';
import { ModalContent } from '../../shared/modal-content';
import { ModalComponent } from 'angular-custom-modal';
import { OptionItem } from '../../shared/types/api-responses.type';
import { RolesService } from '../../services/roles/roles.service';
import { MerchantsService  } from '../../services/merchants/merchants.service';
import { selectCurrentUser } from '../../services/users/user.selectors';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css']
})
export class UsersComponent implements OnInit, OnDestroy {
  @ViewChild('modal') modal!: ModalComponent;

  private destroy$ = new Subject<void>();
  loading = false;
  roles: OptionItem[] = [];
  merchants: OptionItem[] = [];

  modalData: ModalContent = new ModalContent({
    header: 'Confirmation',
    body: 'Are you sure you want to delete this user?',
    showCloseButton: true,
    showConfirmButton: true,
    closeButtonLabel: 'Cancel',
    confirmButtonLabel: 'Delete',
    closeButtonClass: 'btn-secondary',
    confirmButtonClass: 'btn-danger',
  })

  selectedId = '';

  params = new GetUserRequestParams();
  totalRows = 0;

  cols = [
    { field: 'no', title: 'No' },
    { field: 'name', title: 'Name' },
    { field: 'email', title: 'Email' },
    { field: 'phone', title: 'Phone' },
    { field: 'client_name', title: 'Client Name' },
    { field: 'roleName', title: 'Role Name' },
    { field: 'merchantName', title: 'Merchant Name' },
    { field: 'action', title: 'Action', type: 'action' },
  ];

  pageSizeOptions = [5, 10, 25, 50, 100];
  rows: UserData[] = [];

  userAuth$!: Observable<UserData | null>;
  isAdmin$!: Observable<boolean>;

  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private userService: UsersService,
    private readonly roleService: RolesService,
    private readonly merchantService: MerchantsService,
    public storeData: Store<any>,
  ) {
  }

  ngOnInit(): void {
    this.userAuth$ = this.storeData.select(selectCurrentUser);
    this.isAdmin$ = this.userAuth$.pipe(
      map((user) => user?.roles?.some((role) => role.code === 'admin') ?? false)
    );

    this.userAuth$.subscribe((user) => {
      if (user) {
        this.isAdmin$.pipe(
          takeUntil(this.destroy$)
        ).subscribe((isAdmin) => {
          if (isAdmin) {
            this.getRoleData().then();
            this.getMerchantData().then();
          } else {
            this.params.merchantId = user.merchant.id;
          }
        });
        this.getUsers();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUsers(): void {
    this.loading = true;
    this.rows = [];

    this.userService.getUserService(this.params)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (response) => {
          this.rows = this.processDataForTable(response.data);
          this.totalRows = response.totalPage;
        },
        error: (err) => console.error('Error fetching user data:', err)
      });
  }

  processDataForTable(data: UserData[]) {
    return data.map((each, i) => ({
      ...each,
      no: getOffset(this.params.pageNo, this.params.rowPerPage) + i + 1,
      merchantName : each.merchant != null ? each.merchant.name : "",
      roleName: each.roles ? each.roles.map(role => toTitleCase(role.name)).join(', ') : '',
    }));
  }

  changeServer(data: any): void {
    this.params.pageNo = data.current_page;
    this.params.rowPerPage = data.pagesize;
    this.params.filter = data.search;
    this.getUsers();
  }

  onInputChanged(value: string): void {
    console.log('Debounced Input:', value);
    this.params.filter = '';
    if (value) {
      this.params.filter = value;
    }
    this.cdRef.detectChanges();
  }

  onRoleChange(roleOption: OptionItem): void {
    this.params.roleId = null;
    if (roleOption) {
      console.log('Role Change:', roleOption.value);
      this.params.roleId = roleOption.value;
    }
    this.getUsers();
  }

  onMerchantChange(merchantOption: OptionItem): void {
    this.params.merchantId = null;
    if (merchantOption) {
      console.log('Merchant Change:', merchantOption.value);
      console.log('Merchant Change:', merchantOption.label);
      this.params.merchantId = merchantOption.value;
    }
    this.getUsers();
  }

  goToForm(id: string): void {
    if (id === 'new') {
      this.router.navigate(['/users/new']).then();
      return;
    }
    this.router.navigate(['/users/edit', id]).then();
    return;
  }

  private async getRoleData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.roleService.getRoles());
      this.roles = data.map(role => ({ label: role.name, value: role.id }));
    } catch (error) {
      showNotification('error', 'Failed to fetch roles', JSON.stringify(error));
    }
  }

  private async getMerchantData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.merchantService.getMerchants());
      this.merchants = data.map(merchant => ({ label: merchant.name, value: merchant.id }));
    } catch (error) {
      showNotification('error', 'Failed to fetch merchants', JSON.stringify(error));
    }
  }





  async deleteUser(id: string): Promise<void> {
    this.loading = true;

    try {
      const delResp = await firstValueFrom(this.userService.deleteUserById(id));
      showNotification('success', 'Success to delete user', JSON.stringify(delResp));
      this.getUsers();
    } catch (error) {
      console.error('Delete User Error:', error);
    } finally {
      this.loading = false;
    }
  }

  async onModalDeleteConfirm(id: string): Promise<void> {
    await this.deleteUser(id);
    this.modal.close();
  }

  get isCompactFilter(): boolean {
    return this.roles.length === 0 && this.merchants.length === 0;
  }
}
